import { api, apiErrorMessage } from './api-client';
import type { TimeOffAttachmentDto } from './types';

/** PDF and JPG/JPEG only, mirroring the server-side allow-list. */
export const ATTACHMENT_ALLOWED_MIME = new Set(['application/pdf', 'image/jpeg']);
/** 3 MB per file, matching the server-side cap. */
export const ATTACHMENT_MAX_BYTES = 3 * 1024 * 1024;
/** accept="" value and helper-text hint for the file input. */
export const ATTACHMENT_ACCEPT = '.pdf,.jpg,.jpeg';
export const ATTACHMENT_HELP = 'PDF or JPG, up to 3 MB each. Optional.';

/** A client-side reason a picked file is invalid, or null when it passes the pre-check. */
export function attachmentRejectionReason(file: File): string | null {
  if (!ATTACHMENT_ALLOWED_MIME.has(file.type)) {
    return `"${file.name}" is not a supported type. Allowed: PDF, JPG.`;
  }
  if (file.size > ATTACHMENT_MAX_BYTES) {
    return `"${file.name}" exceeds the 3 MB limit.`;
  }
  return null;
}

/** The first invalid file across the list, or null when all pass the pre-check. */
export function firstAttachmentRejection(files: File[]): string | null {
  for (const file of files) {
    const reason = attachmentRejectionReason(file);
    if (reason) {
      return reason;
    }
  }
  return null;
}

/** Uploads one file to a created request; the server re-validates type and size. */
export function uploadTimeOffAttachment(
  requestId: number,
  file: File,
): Promise<TimeOffAttachmentDto> {
  const formData = new FormData();
  formData.append('file', file);
  return api.upload<TimeOffAttachmentDto>(`/v1/time-off/requests/${requestId}/attachments`, formData);
}

/** Uploads every file to a created request in order; rejects on the first failure. */
export async function uploadTimeOffAttachments(requestId: number, files: File[]): Promise<void> {
  for (const file of files) {
    await uploadTimeOffAttachment(requestId, file);
  }
}

/**
 * Uploads every file to a request that was just recorded and answers why that failed, or null. The
 * request exists by then, so a failure is reported instead of failing the submission: submitting again
 * would record the request a second time.
 */
export async function failedAttachmentUpload(requestId: number, files: File[]): Promise<string | null> {
  try {
    await uploadTimeOffAttachments(requestId, files);
    return null;
  } catch (err: unknown) {
    return `Request saved without its attachments. ${apiErrorMessage(err, 'The upload failed.')}`;
  }
}
